import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { t } from 'i18next';
import { useSnackbar } from 'notistack';
import LoadingCenter from '../util/LoadingCenter';

interface Row {
  userId: number;
  name?: string;
  typeId: number;
  typeName?: string;
  remainingHours: number;
  remainingDays: number;
  suggestedHours: number;
  suggestedDays: number;
  patternUnit: string;
  alreadyCarried: boolean;
  amount: string;
}

interface Props {
  fromYear: number;        // год-источник (остаток берётся отсюда)
  onClose: () => void;
  onDone: () => void;
  unitMode?: 'auto' | 'days' | 'hours';
}

// Перенос остатка «в текущий год» (из fromYear в fromYear+1) — список всех видимых сотрудников
const LeaveCarryoverPanel: React.FC<Props> = ({ fromYear, onClose, onDone, unitMode = 'auto' }) => {
  const { enqueueSnackbar } = useSnackbar();
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const toYear = fromYear + 1;
  const dS = t('DShort') as string;
  const hS = t('HShort') as string;

  // Единица показа по строке: auto → из паттерна; иначе принудительно
  const effUnit = (r: { patternUnit: string }) => unitMode === 'auto' ? (r.patternUnit === 'hours' ? 'hours' : 'days') : unitMode;
  const h1 = (v: number) => Math.round((v ?? 0) * 10) / 10;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`hr-leave-adjustment/carryover-preview?fromYear=${fromYear}`);
      setRows((Array.isArray(data) ? data : []).map((x: any) => {
        const patternUnit = x.patternUnit ?? 'days';
        const unit = unitMode === 'auto' ? (patternUnit === 'hours' ? 'hours' : 'days') : unitMode;
        return {
          userId: x.userId, name: x.name, typeId: x.typeId, typeName: x.typeName,
          remainingHours: x.remainingHours ?? 0, remainingDays: x.remainingDays ?? 0,
          suggestedHours: x.suggestedHours ?? 0, suggestedDays: x.suggestedDays ?? 0,
          patternUnit, alreadyCarried: !!x.alreadyCarried,
          amount: String(unit === 'hours' ? (x.suggestedHours ?? 0) : (x.suggestedDays ?? 0)),
        };
      }));
    } catch { setRows([]); } finally { setLoading(false); }
  }, [fromYear, unitMode]);

  useEffect(() => { load(); }, [load]);

  const setAmount = (i: number, v: string) => setRows(rs => rs.map((r, idx) => idx === i ? { ...r, amount: v } : r));

  // Перенести всех сразу
  const save = async () => {
    const items = rows
      .filter(r => !r.alreadyCarried && (Number(r.amount) || 0) > 0)
      .map(r => ({ userId: r.userId, typeId: r.typeId, amount: Number(r.amount), unit: effUnit(r) }));
    if (!items.length) { enqueueSnackbar(t('HrNothingToCarry', 'Nothing to carry over') as string, { variant: 'warning' }); return; }
    setSaving(true);
    try {
      const { data } = await axios.post('hr-leave-adjustment/carryover', { toYear, items });
      enqueueSnackbar(`${t('HrCarriedOver', 'Carried over')}: ${data?.created ?? 0}${data?.skipped ? ` · ${t('HrSkipped', 'skipped')}: ${data.skipped}` : ''}`, { variant: 'success' });
      onDone(); load();
    } catch {
      enqueueSnackbar(t('Error') as string, { variant: 'error' });
    } finally { setSaving(false); }
  };

  // Перенести одного сотрудника
  const saveOne = async (r: Row) => {
    const amount = Number(r.amount) || 0;
    if (amount <= 0) { enqueueSnackbar(t('HrNothingToCarry', 'Nothing to carry over') as string, { variant: 'warning' }); return; }
    setSaving(true);
    try {
      await axios.post('hr-leave-adjustment/carryover', { toYear, items: [{ userId: r.userId, typeId: r.typeId, amount, unit: effUnit(r) }] });
      enqueueSnackbar(t('Saved') as string, { variant: 'success' });
      onDone(); load();
    } catch {
      enqueueSnackbar(t('Error') as string, { variant: 'error' });
    } finally { setSaving(false); }
  };

  const lblCell: React.CSSProperties = { fontSize: 10, fontWeight: 800, letterSpacing: '.03em', textTransform: 'uppercase', color: 'var(--text-color-calendar-week)', padding: '6px 10px', borderBottom: '1px solid var(--border-input)' };

  return (
    <div className="ts-card" style={{ display: 'flex', flexDirection: 'column', overflow: 'hidden', height: '100%', minHeight: 0 }}>
      {/* Header — название типа + годы */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 14px', background: 'transparent', borderBottom: 'var(--border-input)' }}>
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{ fontWeight: 800, fontSize: 13, color: 'var(--text-color)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>↪ {t('HrCarryover', 'Rollover')}{rows[0]?.typeName ? ` ${rows[0].typeName}` : ''}</div>
          <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-color-calendar-week)', fontVariantNumeric: 'tabular-nums' }}>{fromYear} → {toYear}</div>
        </div>
        <span style={{ cursor: 'pointer', opacity: 0.5, fontSize: 16 }} onClick={onClose}>✕</span>
      </div>

      {/* Body */}
      <div className="thin-scroll" style={{ flex: 1, overflow: 'auto', minHeight: 0 }}>
        {loading ? (
          <LoadingCenter />
        ) : rows.length === 0 ? (
          <div style={{ padding: 20, textAlign: 'center', color: 'var(--text-color-calendar-week)' }}>{t('Empty')}</div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={{ ...lblCell, textAlign: 'left' }}>{t('Name')}</th>
                <th style={{ ...lblCell, textAlign: 'right' }}>{t('HrRemaining')}</th>
                <th style={{ ...lblCell, textAlign: 'right', width: 118 }}>{t('HrToCarry', 'To carry')}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => {
                const isH = effUnit(r) === 'hours';
                return (
                  <tr key={`${r.userId}-${r.typeId}`}>
                    <td style={{ padding: '7px 10px', fontSize: 13, borderBottom: '1px solid var(--border-input-one)' }}>{r.name ?? `#${r.userId}`}</td>
                    <td style={{ padding: '7px 10px', fontSize: 13, textAlign: 'right', borderBottom: '1px solid var(--border-input-one)', fontVariantNumeric: 'tabular-nums' }}>
                      {isH ? `${h1(r.remainingHours)}${hS}` : `${r.remainingDays}${dS}`}
                    </td>
                    <td style={{ padding: '7px 10px', textAlign: 'right', borderBottom: '1px solid var(--border-input-one)' }}>
                      {r.alreadyCarried ? (
                        <span style={{ fontSize: 11, color: 'var(--text-color-calendar-week)' }}>{t('HrAlreadyCarried', 'already carried')}</span>
                      ) : (
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                          <input type="number" min="0" step="0.25" className="timesheet-edit-input" style={{ width: 72, textAlign: 'right' }}
                            value={r.amount} onChange={e => setAmount(i, e.target.value)} />
                          <span style={{ fontSize: 11, color: 'var(--text-color-calendar-week)', width: 14 }}>{isH ? hS : dS}</span>
                          <button type="button" className="btn-icon" title={t('HrCarryover', 'Rollover') as string} disabled={saving} onClick={() => saveOne(r)}>↪</button>
                        </span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Footer — как у DataTable (.st-foot) */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 'var(--space-l3)', minHeight: 38, padding: '6px 14px', borderTop: 'var(--border-input)', background: 'transparent' }}>
        <button type="button" className="dt-btn-ghost" style={{ fontSize: 12, padding: '5px 12px' }} onClick={onClose}>{t('Cancel')}</button>
        <button type="button" className="btn-primary" style={{ fontSize: 12, padding: '5px 12px' }} onClick={save} disabled={saving || loading}>↪ {t('HrCarryoverAll', 'Rollover all')}</button>
      </div>
    </div>
  );
};

export default LeaveCarryoverPanel;
